import i18next from 'i18next';
import { MarketCode } from '../@types/MarketCode';
import ready from '../utils/ready';

const getLanguage = (marketCode?: MarketCode) => {
  switch (marketCode) {
    case 'NO':
      return 'nb-NO';
    case 'SE':
      return 'sv-SE';
    default:
      return 'en-US';
  }
};

const initializeLocale = () => {
  if (i18next.isInitialized) return;
  const market = document.documentElement
    .getAttribute('data-market-code')
    ?.toUpperCase() as MarketCode;
  const locale = getLanguage(market);

  i18next.init({
    lng: locale || 'en-US',
    fallbackLng: 'en-US',
    debug: false,
    resources: {
      'sv-SE': {
        translation: {
          filter: {
            manufacturer: 'Tillverkare',
            modelSeries: 'Modell',
            fuelType: 'Drivmedel',
            gearboxType: 'Växellåda',
            branch: 'Anläggning',
            color: 'Färg',
            environmentClass: 'Miljöklass',
            'properties.segment': 'Segment',
            drivingWheel: 'Drivhjul',
            price: 'Pris',
            mileage: 'Miltal',
            modelYear: 'Modellår',
            odometerValueAsKm: 'Mätarställning',
            leasingPrice: 'Privatleasing',
          },
          currency: {
            default: 'kr',
            monthly: 'kr/mån',
          },
          category: {
            basic: 'Basuppgifter',
            engineAndPerformance: 'Motor & Prestanda',
          },
          subCategory: {
            engine: 'Motor',
          },
          customProperty: {
            manufacturer: {
              displayName: 'Varumärke',
              hint: '',
            },
            modelSeries: {
              displayName: 'Modell',
              hint: '',
            },
            modelName: { displayName: 'Version', hint: 'Vilket utförande av bilmodellen det är.' },
            modelYear: {
              displayName: 'Modellår',
              hint: 'Modellår sätts av tillverkaren. Handlaren ändrar ibland modellåret för att matcha transportstyrelsens definition för <i>årsmodell</i>. Definitionen för årsmodell innebär att bilar tillverkade andra halvan av året får nästa års modellår.',
            },
            manufactureYear: { displayName: 'Tillverkningsår', hint: 'Året bilen tillverkades.' },
            odometer: { displayName: 'Mätarställning', hint: '' },
            gearboxType: { displayName: 'Växellåda', hint: '' },
            registrationNumber: { displayName: 'Registreringsnummer', hint: '' },
            fuelType: {
              displayName: 'Bränslegrupp',
              hint: 'Vad bilen ska tankas med och drivs av.',
            },
            enginePower: { displayName: 'Effekt', unit: 'hk' },
            numberOfDoors: { displayName: 'Dörrar' },
          },
          odometer: {
            default: 'mil',
            defaultYearly: 'mil/år',
            Kilometer: 'km',
            ScandinavianMile: 'mil',
          },
          common: {
            yes: 'Ja',
            no: 'Nej',
            logotype: 'Logotyp',
            close: 'Stäng',
            showMore: 'Visa mer',
            readMore: 'Läs mer',
            showMoreInformation: 'Visa mer information',
            showLessInformation: 'Visa mindre information',
            choose: 'Välj',
            address: 'Adress',
            mondayShort: 'Mån',
            tuesdayShort: 'Tis',
            wednesdayShort: 'Ons',
            thursdayShort: 'Tor',
            fridayShort: 'Fre',
            saturdayShort: 'Lör',
            sundayShort: 'Sön',
            moreInformation: 'Mer information',
            lessInformation: 'Mindre information',
            anErrorHasOccured: 'Ett fel har inträffat',
            opensInNewTab: 'Öppnas i ny flik',
            open: 'Öppna',
            imageIndex: 'Bild {{index}}',
            showAll: 'Visa alla',
            months: 'Månader',
            monthUnitShort: 'mån',
          },
          item: {
            relatedVehicles: 'Relaterade fordon',
            latestVehicles: 'Senast inkomna',
            readMoreInNewTab: 'Läs mer (öppnas i ny flik)',
            moreInfoAboutSubject: 'Mer information om {{subject}}',
            dataNotAvailable: 'Uppgift saknas',
            showOnGoogleMaps: 'Visa på Google Maps',
            googleMapsMapAlt: 'Karta',
            googleMapsPlaceholderInit: 'Visa karta',
            demoCar: 'Demobil',
            demoCarDescription:
              'En demobil är en bil som stått i utställning eller som på annat sätt använts för att visa upp en viss modell. Bilarna är ofta välutrustade och har blivit väl omhändertagna. Dessutom har de vanligtvis gått få mil och har fortfarande nybilsgarantierna kvar.',
            carData: 'Biluppgifter',
            carDataDescription: 'Information direkt från Transportstyrelsen och tillverkaren.',
            equipment: 'Utrustning',
            equipmentDescription: 'Kompletterande uppgifter om bilen.',
            documents: 'Dokument',
            documentTypes: {
              tireEnergyDeclaration: 'Energideklaration för däck',
              goodsDeclaration: 'Varudeklaration',
              other: 'Övriga dokument',
            },
            carIsReserved: 'Bilen är reserverad',
            carIsReservedDescription: 'Denna bil är reserverad av en annan köpare.',
            carIsReservedModalBody: 'Denna bil är reserverad av en annan köpare.',
            centralWarehouseCar: 'Centrallagerbil',
            centralWarehouseCarDescription:
              'Denna bil tillhör ett centrallager och går att köpa genom flera anläggningar. Byt anläggning för att visa kontaktuppgifter till just den anläggningen.',
            centralWarehouseCarDescriptionShort:
              'Denna bil tillhör ett centrallager och går att köpa genom flera anläggningar.',
            clickHereToShowContactToAnotherBranch:
              ' Klicka här för att visa kontaktuppgifter till en annan anläggning',
            switchBranch: 'Byt anläggning',
            switchBranchDescription: 'Välj den anläggning du vill visa kontaktuppgifter för.',
            chooseBranch: 'Välj anläggning',
            chooseSelectedBranch: 'Välj vald anläggning',
            availableFromDate: 'Tillgänglig från {{date}}',
            includingShort: 'Inkl.',
            usedWarrany: 'Begagnatgaranti',
            homeDelivery: 'Hemleverans',
            thisCarIsAvailableAtOurBranch: 'Den här bilen finns på vår anläggning i {{location}}',
            contact: 'Kontakt',
            start360Exterior: 'Starta 360° exteriört',
            start360Interior: 'Starta 360° interiört',
            openingHoursToday: 'Öppetider idag',
            closedToday: 'Stängt idag',
            open: 'Öppet',
            closed: 'Stängt',
            addInsurance: 'Välj till försäkring',
            getInsuranceQuote: 'Få prisförslag',
            insurance: 'Försäkring',
            readMoreAboutInsuranceInNewTab: 'Läs mer om försäkringen (öppnas i ny flik)',
            personalNumber: 'Personnummer',
            estimatedAnualMileage: 'Uppskattad körsträcka per år',
            showInsurances: 'Visa försäkringar',
            savePersonalNumberOnThisDevice: 'Spara personnummer på denna enhet',
            savePersonalNumberOnThisDeviceHelp:
              'Spara personnummer på denna dator för att direkt visa försäkringskostnaderna i Wayke. Personnumret lagras inte hos Wayke utan finns bara sparad i din webbläsare.',
            couldNotfetchInsurances: 'Kunde inte hämta försäkringar. Vänligen försök igen.',
            openPrePurchaseInformationInNewTab: 'Förköpsinformation och villkor (öppnas i ny flik)',
            insuranceIncludes: 'Försäkringen innehåller',
            addInsuranceHeading: 'Lägg till',
            financialOptions: {
              otherFinancialOptions: 'Andra finansieringsalternativ',
              privateLease: 'Privatleasa',
              businessLease: 'Företagsleasa',
              monthlyCost: 'Månadskostnad',
              bindingTime: 'Bindningstid',
              annualMileage: 'Årlig körsträcka',
              payInInstallments: 'Delbetala',
              payInInstallmentsDetailed: 'Delbetala {{amount}} i {{duration}} mån',
              disclaimer: 'Beräknat på {{interest}} ränta.',
              loanOptions: 'Lånealternativ',
              downPayment: 'Kontantinsats',
              duration: 'Avbetalning',
              residual: 'Restskuld',
              interest: 'Ränta',
              effectiveinterest: 'Effektiv ränta',
              setupFee: 'Uppläggningskostnad',
              administrationFee: 'Administrativa avgifter',
              totalCreditCost: 'Total kreditkostnad',
              loanOptionsDisclaimer:
                'Beräknat på {{interest}} % ränta (effektivt {{effectiveInterest}} %).',
              loanOptionsDisclaimerMileage:
                'Beräknat på {{interest}} % ränta (effektivt {{effectiveInterest}} %) och en årlig körsträcka om {{mileage}} mil.',
            },
            actions: {
              emailSubject: '{{hostName}} – Jag är intresserad av {{vehicle}}',
              openform: 'Öppna formulär',
              buyOnline: 'Köp bilen online',
              showEmail: 'Visa mailadress',
              showPhoneNumber: 'Visa telefonnummer',
              callNumber: 'Ring',
              sendEmailTo: 'Skicka ett mail till',
            },
            accessories: {
              accessories: 'Tillbehör',
              accessoriesDescription: 'Tillbehör som passar till detta fordon.',
              price: 'Kontantpris',
              oldPrice: 'Tidigare pris',
              installationCost: 'Monteringskostnad',
              articleNumberShort: 'Art. nr.',
              moreInformationAboutAccessory: 'Mer information om {{accessory}}',
            },
          },
          productCard: {
            price: 'Pris',
            privateLeasing: 'Privatleasing',
            priceExclVat: 'Exkl. moms',
          },
          search: {
            filter: 'Filter',
            sort: 'Sortera',
            showMore: {
              text: 'Visa fler',
              title: 'Visa fler resultat',
            },
            filterButton: {
              text: 'Filtrera',
              title: 'Visa filter',
            },
            sortButton: {
              text: 'Sortera',
              title: 'Välj sorteringsordning',
            },
            searchResult: 'Sökresultat',
            types: {
              cars: 'Bilar',
              cmsContent: 'Innehåll',
            },
            snackbarNoSearchResults: {
              heading: 'Inga resultat',
              body: 'Sökningen gav inga resultat',
            },
            snackBarClearFreeText: {
              heading: 'Inga resultat',
              body: 'Rensa fritexten för att se alla filter',
            },
            sortOptions: {
              relevance: 'Relevans',
              publishedAsc: 'Publicerad (äldst)',
              publishedDesc: 'Publicerad (senast)',
              priceAsc: 'Pris (lägst)',
              priceDesc: 'Pris (högst)',
              modelAsc: 'Årsmodell (äldst)',
              modelDesc: 'Årsmodell (nyast)',
              mileageAsc: 'Miltal (lägst)',
              mileageDesc: 'Miltal (högst)',
              leasingAsc: 'Privatleasing (lägst)',
              leasingDesc: 'Privatleasing (högst)',
              companyLeasingAsc: 'Företagsleasing (lägst)',
              companyLeasingDesc: 'Företagsleasing (högst)',
            },
            clearAllFilters: {
              text: 'Rensa alla filter',
              title: 'Rensa alla filter',
            },
            showNumberOfCustomUnit: 'Visa {{count}} {{unit}}',
            result: {
              cmsContent: {
                singular: 'Resultat',
                plural: 'Resultat',
              },
              cars: {
                singular: 'Bil',
                plural: 'Bilar',
              },
            },
            freeText: 'Fritext',
            from: 'Från',
            to: 'Till',
            min: 'Min',
            max: 'Max',
          },
          searchBar: {
            title: 'Sök',
            placeholder: 'Sök din bil',
            button: {
              text: 'Sök',
              title: 'Sök',
            },
            searchResults: 'Sökträffar',
            showAllRsults: 'Visa alla bilar',
          },
          lead: {
            openForm: 'Öppna formulär',
            messageFormTitle: 'Skicka meddelande',
            callFormTitle: 'Bli uppringd',
            firstName: 'Förnamn',
            lastName: 'Efternamn',
            email: 'E-postadress',
            phoneNumber: 'Telefonnummer',
            message: 'Meddelande',
            tradeInCar: 'Inbytesbil',
            registrationNumber: 'Registreringsnummer',
            mileage: 'Miltal',
            send: 'Skicka',
            close: 'Stäng',
            validationMessages: {
              requiredFirstName: 'Förnamn krävs',
              requiredLastName: 'Efternamn krävs',
              requiredEmail: 'E-postadress krävs',
              invalidEmail: 'Ogiltig e-postadress',
              maxMessage: 'Meddelandet får inte överstiga 500 tecken',
              requiredRegNo: 'Registreringsnummer krävs om du har inbytesbil',
              invalidRegNo: 'Ogiltigt registreringsnummer, ange det som t.ex. ABC123',
              requiredMileage: 'Miltal krävs om du har inbytesbil',
              requiredPhoneNumber: 'Telefonnummer krävs',
            },
            confirmationTitle: 'Tack!',
            confirmationMessage: 'Ditt meddelande har skickats. Vi kontaktar dig inom kort.',
            confirmationClose: 'OK',
          },
          other: {
            loading: 'Laddar...',
            error404: '404',
            showingNumberOfResults_one: 'Visar {{current}} av {{count}} resultat',
            showingNumberOfResults_other: 'Visar {{current}} av {{count}} resultat',
            closeModal: 'Stäng modal',
            closePanel: 'Stäng panel',
            goToHeading: 'Gå till {{heading}}',
            openDoumentt: 'Öppna {{document}}',
            previousImage: 'Föregående bild',
            nextImage: 'Nästa bild',
            goToImageIndex: 'Gå till bild {{index}}',
            phoneNumber: 'Telefonnummer',
            showPhoneNumber: 'Visa telefonnummer',
            callPhoneNumber: 'Ring {{phoneNumber}}',
            price: 'Pris',
            privateLeasingPrice: 'Privatleasing',
            priceExclVat: 'Exkl. moms',
            video: 'Video',
            playVideo: 'Spela video',
            searchResults: 'Sökträffar',
            showAllCars: 'Visa alla bilar',
          },
        },
      },
      'nb-NO': {
        translation: {
          filter: {
            manufacturer: 'Produsent',
            modelSeries: 'Modell',
            fuelType: 'Drivstofftype',
            gearboxType: 'Girkassetype',
            branch: 'Anlegget',
            color: 'Farge',
            environmentClass: 'Miljøklasse',
            'properties.segment': 'Segment',
            drivingWheel: 'Drivhjul',
            price: 'Pris',
            mileage: 'Kilometerstand',
            modelYear: 'Modellår',
            odometerValueAsKm: 'Kilometerstand',
            leasingPrice: 'Privatleasing',
          },
          currency: {
            default: 'kr',
            monthly: 'kr/mnd',
          },
          category: {
            basic: 'Grunnopplysninger',
            engineAndPerformance: 'Motor og ytelse',
          },
          subCategory: {
            engine: 'Motor',
          },
          customProperty: {
            manufacturer: {
              displayName: 'Produsent',
              hint: '',
            },
            modelSeries: {
              displayName: 'Modell',
              hint: '',
            },
            modelName: { displayName: 'Versjon', hint: 'Hvilken versjon av bilmodellen det er.' },
            modelYear: {
              displayName: 'Årsmodell',
              hint: 'Modellåret settes av produsenten. Forhandlere endrer av og til modellåret for å matche transportmyndighetens definisjon av modellåret. Definisjonen av modellåret innebærer at biler produsert i den siste halvdelen av året får neste års modellår.',
            },
            manufactureYear: { displayName: 'Produksjonsår', hint: 'Året bilen ble produsert.' },
            odometer: { displayName: 'Kilometerstand', hint: '' },
            gearboxType: { displayName: 'Girkasse', hint: '' },
            registrationNumber: { displayName: 'Registreringsnummer', hint: '' },
            fuelType: {
              displayName: 'Drivstofftype',
              hint: 'Hva bilen skal tankes med og drives av.',
            },
            enginePower: { displayName: 'Effekt', unit: 'hk' },
            numberOfDoors: { displayName: 'Dører' },
          },
          odometer: {
            default: 'km',
            defaultYearly: 'km/år',
            Kilometer: 'km',
            ScandinavianMile: 'mil',
          },
          common: {
            yes: 'Ja',
            no: 'nei',
            logotype: 'Logotype',
            close: 'Close',
            showMore: 'Vis mer',
            readMore: 'Les mer',
            showMoreInformation: 'Vis mer informasjon',
            showLessInformation: 'Vis mindre informasjon',
            choose: 'Velg',
            address: 'Adresse',
            mondayShort: 'man',
            tuesdayShort: 'tirs',
            wednesdayShort: 'Wednesday',
            thursdayShort: 'Thursday',
            FridayShort: 'fre',
            saturdayShort: 'lørdag',
            sundayShort: 'Søn',
            moreInformation: 'Mer informasjon',
            lessInformation: 'Mindre informasjon',
            anErrorHasOccured: 'Det har oppstått en feil',
            opensInNewTab: 'Åpnes i ny fane',
            open: 'Åpne',
            imageIndex: 'Bilde {{indeks}}',
            showAll: 'Vis alle',
            months: 'Måneder',
            monthUnitShort: 'mnd',
          },
          item: {
            relatedVehicles: 'Relaterte kjøretøy',
            latestVehicles: 'Sist mottatt',
            readMoreInNewTab: 'Les mer (åpnes i ny fane)',
            moreInfoAboutSubject: 'Mer informasjon om {{subject}}',
            dataNotAvailable: 'Data ikke tilgjengelig',
            showOnGoogleMaps: 'Vis på Google Maps',
            googleMapsMapAlt: 'Kart',
            googleMapsPlaceholderInit: 'Vis kart',
            demoCar: 'Demo Car',
            demoCarDescription:
              'En demobil er en bil som har vært utstilt eller som har blitt brukt på annen måte for å vise frem en bestemt modell. Bilene er ofte godt utstyrt og har blitt tatt godt vare på. Dessuten har de vanligvis bare noen få kilometer på seg og har fortsatt garantien på nybilen.',
            carData: 'Bilopplysninger',
            carDataDescription: 'Informasjon direkte fra Statens Vegvesen og produsenten.',
            equipment: 'Utstyr',
            equipmentDescription: 'Ytterligere informasjon om bilen.',
            documents: 'Dokumenter',
            documentTypes: {
              tireEnergyDeclaration: 'Energideklarasjon for dekk',
              goodsDeclaration: 'Varedeklarasjon',
              other: 'Andre dokumenter',
            },
            carIsReserved: 'Bilen er reservert',
            carIsReservedDescription: 'Denne bilen er reservert av en annen kjøper.',
            carIsReservedModalBody: 'Denne bilen er reservert av en annen kjøper.',
            centralWarehouseCar: 'Sentrallagerbil',
            centralWarehouseCarDescription:
              'Denne bilen tilhører et sentrallager og kan kjøpes gjennom flere fasiliteter. Endre anlegget for å vise kontaktinformasjon for det bestemte anlegget.',
            centralWarehouseCarDescriptionShort:
              'Denne bilen tilhører et sentrallager og kan kjøpes gjennom flere fasiliteter.',
            clickHereToShowContactToAnotherBranch:
              'Klikk her for å se kontaktdetaljer for et annet anlegg',
            switchBranch: 'Bytt gren',
            switchBranchDescription: 'Velg filialen du vil vise kontaktdetaljer for.',
            selectBranch: 'Choose Branch',
            chooseSelectedBranch: 'Velg valgt gren',
            availableFromDate: 'Tilgjengelig fra {{date}}',
            includingShort: 'Inkl.',
            usedWarrany: 'Bruktgaranti',
            homeDelivery: 'Hjemlevering',
            thisCarIsAvailableAtOurBranch:
              'Denne bilen er tilgjengelig i vår filial i {{location}}',
            contact: 'Kontakt',
            start360Exterior: 'Start 360° eksteriør',
            start360Interior: 'Start 360° interiør',
            openingHoursToday: 'Åpningstider i dag',
            closedToday: 'Stengt i dag',
            open: 'Åpne',
            closed: 'Stengt',
            addInsurance: 'Velg til forsikring',
            getInsuranceQuote: 'Få tilbud',
            insurance: 'Forsikring',
            readMoreAboutInsuranceInNewTab: 'Les mer om forsikring (åpnes i ny fane)',
            personalNumber: 'Personnummer',
            estimatedAnualMileage: 'Estimert kjørelengde per år',
            showInsurances: 'Vis forsikringer',
            savePersonalNumberOnThisDevice: 'Lagre personlig nummer på denne enheten',
            savePersonalNumberOnThisDeviceHelp:
              'Lagre personnummeret på denne datamaskinen for å direkte se forsikringskostnadene i Wayke. Personnummeret lagres ikke hos Wayke, men lagres kun i nettleseren din.',
            couldNotfetchInsurances: 'Kunne ikke hente forsikringer. Vær så snill, prøv på nytt.',
            openPrePurchaseInformationInNewTab: 'Informasjon og vilkår før kjøp (åpnes i ny fane)',
            insuranceIncludes: 'Forsikringen inkluderer',
            addInsuranceHeading: 'Add',
            financialOptions: {
              otherFinancialOptions: 'Andre finansieringsalternativer',
              privateLease: 'PrivateLease',
              businessLease: 'Etagslease',
              monthlyCost: 'Månedlig kostnad',
              bindingTime: 'Binding Time',
              annualMileage: 'Annual Mileage',
              payInInstallments: 'Betal i avdrag',
              payInInstallmentsDetailed: 'Betal delvis {{amount}} i {{duration}} måneder',
              disclaimer: 'Beregnet til {{interest}} rente.',
              loanOptions: 'Lånealternativer',
              downPayment: 'Egenkapital',
              duration: 'Nedbetalingstid',
              residual: 'Restgjeld',
              interest: 'Nominell rente',
              effectiveinterest: 'Effektiv rente',
              setupFee: 'Etablerings- og tinglysningsgebyr',
              administrationFee: 'Månedsgebyr',
              totalCreditCost: 'Totale kredittkostnader',
              loanOptionsDisclaimer:
                'Beregnet til {{interest}} % rente (effektiv {{effectiveInterest}} %).',
              loanOptionsDisclaimerMileage:
                'Beregnet på {{interest}} % rente (effektiv {{effectiveInterest}} %) og en årlig kjørelengde på {{mileage}} miles.',
            },
            actions: {
              emailSubject: '{{hostName}} - Jeg er interessert i {{vehicle}}',
              openform: 'Åpent skjema',
              buyOnline: 'Kjøp bilen på nett',
              showEmail: 'Vis e-postadresse',
              showPhoneNumber: 'Vis telefonnummer',
              callNumber: 'Ring',
              sendEmailTo: 'Send en e-post til',
            },
            accessories: {
              accessories: 'Tilbehør',
              accessoriesDescription: 'Tilbehør som passer for dette kjøretøyet.',
              price: 'Kontantpris',
              oldPrice: 'Forrige pris',
              installationCost: 'Installasjonskostnad',
              articleNumberShort: 'Art. Nei.',
              moreInformationAboutAccessory: 'Mer informasjon om {{accessory}}',
            },
          },
          productCard: {
            price: 'Pris',
            privateLeasing: 'Privatleasing',
            priceExclVat: 'Ekskl. mva',
          },
          search: {
            filter: 'Filter',
            sort: 'Sorter',
            showMore: {
              text: 'Vis mer',
              title: 'Vis flere resultater',
            },
            filterButton: {
              text: 'Filter',
              title: 'Vis filter',
            },
            sortButton: {
              text: 'Sorter',
              title: 'Velg sorteringsrekkefølge',
            },
            searchResult: 'Søkeresultat',
            types: {
              cars: 'Biler',
              cmsContent: 'Innhold',
            },
            snackbarNoSearchResults: {
              heading: 'Ingen resultater',
              body: 'Søket ga ingen resultater',
            },
            snackBarClearFreeText: {
              heading: 'Ingen resultater',
              body: 'Tøm friteksten for å se alle filtre',
            },
            sortOptions: {
              relevance: 'Relevans',
              publishedAsc: 'Publisert (eldste)',
              publishedDesc: 'Publisert (siste)',
              priceAsc: 'Pris (laveste)',
              priceDesc: 'Pris (maksimum)',
              modelAsc: 'Årsmodell (eldste)',
              modelDesc: 'Årsmodell (nyeste)',
              mileageAsc: 'Kilometerstand (lavest)',
              mileageDesc: 'Kilometerstand (maksimum)',
              leasingAsc: 'Privatleasing (lavest)',
              leasingDesc: 'Privatleasing (høyest)',
              companyLeasingAsc: 'Bedriftsleasing (lavest)',
              companyLeasingDesc: 'Bedriftsleasing (høyest)',
            },
            clearAllFilters: {
              text: 'Tøm alle filtre',
              title: 'Tøm alle filtre',
            },
            showNumberOfCustomUnit: 'Vis {{count}} {{unit}}',
            result: {
              cmsContent: {
                singular: 'Resultat',
                plural: 'Resultater',
              },
              cars: {
                singular: 'Bil',
                plural: 'Biler',
              },
            },
            freeText: 'Gratis tekst',
            from: 'Fra',
            to: 'Til',
            min: 'Min',
            max: 'Max',
          },
          searchBar: {
            title: 'Søk',
            placeholder: 'Søk i bilen din',
            button: {
              text: 'Søk',
              title: 'Søk',
            },
            searchResults: 'Søkeresultater',
            showAllRsults: 'Vis alle biler',
          },
          lead: {
            openForm: 'Åpent skjema',
            messageFormTitle: 'Send melding',
            callFormTitle: 'Bli oppringt',
            firstName: 'Fornavn',
            lastName: 'Etternavn',
            email: 'E-postadresse',
            phoneNumber: 'Telefonnummer',
            message: 'Melding',
            tradeInCar: 'Innbyttebil',
            registrationNumber: 'Registreringsnummer',
            mileage: 'Kilometerstand',
            send: 'Send',
            close: 'Lukk',
            validationMessages: {
              requiredFirstName: 'Fornavn er påkrevd',
              requiredLastName: 'Etternavn er påkrevd',
              requiredEmail: 'E-postadresse er påkrevd',
              invalidEmail: 'Ugyldig e-postadresse',
              maxMessage: 'Meldingen kan ikke overskride 500 tegn',
              requiredRegNo: 'Registreringsnummer er påkrevd hvis du har innbyttebil',
              invalidRegNo: 'Ugyldig registreringsnummer, oppgi det som f.eks. AB12345',
              requiredMileage: 'Kilometerstand er påkrevd hvis du har innbyttebil',
              requiredPhoneNumber: 'Telefonnummer er påkrevd',
            },
            confirmationTitle: 'Takk!',
            confirmationMessage: 'Meldingen din er sendt. Vi kontakter deg snart.',
            confirmationClose: 'OK',
          },
          other: {
            loading: 'Laster...',
            error404: '404',
            showingNumberOfResults_one: 'Viser {{current}} av {{count}} resultater',
            showingNumberOfResults_other: 'Viser {{current}} av {{count}} resultater',
            closeModal: 'Close Modal',
            closePanel: 'Lukk panel',
            goToHeading: 'Gå til {{heading}}',
            openDoumentt: 'Åpne {{dokument}}',
            previousImage: 'Forrige bilde',
            nextImage: 'Neste bilde',
            goToImageIndex: 'Gå til bilde {{indeks}}',
            phoneNumber: 'Phone Number',
            showPhoneNumber: 'Vis telefonnummer',
            callPhoneNumber: 'Ring {{phoneNumber}}',
            price: 'Pris',
            privateLeasingPrice: 'Privatleasing',
            priceExclVat: 'Ekskl. mva',
            video: 'Video',
            playVideo: 'Spill av video',
            searchResults: 'Søkeresultater',
            showAllCars: 'Vis alle biler',
          },
        },
      },
      'en-US': {
        translation: {
          filter: {
            manufacturer: 'Manufacturer',
            modelSeries: 'Model',
            fuelType: 'Fuel Type',
            gearboxType: 'Gearbox Type',
            branch: 'Branch',
            color: 'Color',
            environmentClass: 'Environmental Class',
            'properties.segment': 'Segment',
            drivingWheel: 'Driving Wheel',
            price: 'Price',
            mileage: 'Mileage',
            modelYear: 'Model Year',
            odometerValueAsKm: 'Meter setting',
            leasingPrice: 'Private leasing',
          },
          currency: {
            default: 'kr',
            monthly: 'kr/month',
          },
          category: {
            basic: 'Basic Information',
            engineAndPerformance: 'Engine & Performance',
          },
          subCategory: {
            engine: 'Engine',
          },
          customProperty: {
            manufacturer: {
              displayName: 'Brand',
              hint: '',
            },
            modelSeries: {
              displayName: 'Model',
              hint: '',
            },
            modelName: { displayName: 'Version', hint: 'Which version of the car model it is.' },
            modelYear: {
              displayName: 'Model year',
              hint: 'The model year is set by the manufacturer. Dealers sometimes change the model year to match the transport authority`s definition of the model year. The definition of the model year means that cars manufactured in the latter half of the year are given the next year`s model year.',
            },
            manufactureYear: {
              displayName: 'Manufacture year',
              hint: 'The year the car was manufactured.',
            },
            odometer: { displayName: 'Odometer reading', hint: '' },
            gearboxType: { displayName: 'Gearbox', hint: '' },
            registrationNumber: { displayName: 'Registration number', hint: '' },
            fuelType: {
              displayName: 'Fuel type',
              hint: 'What the car should be fueled with and powered by.',
            },
            enginePower: { displayName: 'Power', unit: 'hp' },
            numberOfDoors: { displayName: 'Doors' },
          },
          odometer: {
            default: 'km',
            defaultYearly: 'km/year',
            Kilometer: 'km',
            ScandinavianMile: 'mil',
          },
          common: {
            yes: 'Yes',
            no: 'No',
            logotype: 'Logotype',
            close: 'Close',
            showMore: 'Show more',
            readMore: 'Read more',
            showMoreInformation: 'Show more information',
            showLessInformation: 'Show less information',
            choose: 'Choose',
            address: 'Address',
            mondayShort: 'Mon',
            tuesdayShort: 'Tue',
            wednesdayShort: 'Wed',
            thursdayShort: 'Thu',
            fridayShort: 'Fri',
            saturdayShort: 'Sat',
            sundayShort: 'Sun',
            moreInformation: 'More information',
            lessInformation: 'Less information',
            anErrorHasOccured: 'An error has occurred',
            opensInNewTab: 'Opens in new tab',
            open: 'Open',
            imageIndex: 'Image {{index}}',
            showAll: 'Show all',
            months: 'Months',
            monthUnitShort: 'mo.',
          },
          item: {
            relatedVehicles: 'Related Vehicles',
            latestVehicles: 'Latest received',
            readMoreInNewTab: 'Read more (opens in new tab)',
            moreInfoAboutSubject: 'More information about {{subject}}',
            dataNotAvailable: 'Data not available',
            showOnGoogleMaps: 'Show on Google Maps',
            googleMapsMapAlt: 'Map',
            googleMapsPlaceholderInit: 'Show Map',
            demoCar: 'Demo Car',
            demoCarDescription:
              'A demo car is a car that has been on display or that has been used in some other way to show off a certain model. The cars are often well equipped and have been well looked after. Also, they usually have only a few miles on them and still have their new car warranties.',
            carData: 'Car Data',
            carDataDescription:
              'Information directly from the Swedish Transport Agency and the manufacturer.',
            equipment: 'Equipment',
            equipmentDescription: 'Additional information about the car.',
            documents: 'Documents',
            documentTypes: {
              tireEnergyDeclaration: 'Energy declaration for tyres',
              goodsDeclaration: 'Goods declaration',
              other: 'Other documents',
            },
            carIsReserved: 'The car is reserved',
            carIsReservedDescription: 'This car is reserved by another buyer.',
            carIsReservedModalBody: 'This car is reserved by another buyer.',
            centralWarehouseCar: 'Central warehouse car',
            centralWarehouseCarDescription:
              'This car belongs to a central warehouse and can be purchased through several facilities. Change facility to display contact information for that particular facility.',
            centralWarehouseCarDescriptionShort:
              'This car belongs to a central warehouse and can be purchased through several facilities.',
            clickHereToShowContactToAnotherBranch:
              'Click here to view contact details for another facility',
            switchBranch: 'Switch Branch',
            switchBranchDescription: 'Select the branch you want to display contact details for.',
            chooseBranch: 'Choose branch',
            chooseSelectedBranch: 'Choose selected branch',
            availableFromDate: 'Available from {{date}}',
            includingShort: 'Incl.',
            usedWarrany: 'Used Warranty',
            homeDelivery: 'Home delivery',
            thisCarIsAvailableAtOurBranch: 'This car is available at our branch in {{location}}',
            contact: 'Contact',
            start360Exterior: 'Start 360° exterior',
            start360Interior: 'Start 360° interior',
            openingHoursToday: 'Opening hours today',
            closedToday: 'Closed today',
            open: 'Open',
            closed: 'Closed',
            addInsurance: 'Select insurance',
            getInsuranceQuote: 'Get Quote',
            insurance: 'Insurance',
            readMoreAboutInsuranceInNewTab: 'Read more about insurance (opens in new tab)',
            personalNumber: 'Personal number',
            estimatedAnnualMileage: 'Estimated mileage per year',
            showInsurances: 'Show insurances',
            savePersonalNumberOnThisDevice: 'Save personal number on this device',
            savePersonalNumberOnThisDeviceHelp:
              'Save social security number on this computer to directly view the insurance costs in Wayke. The social security number is not stored at Wayke, but is only saved in your browser.',
            couldNotfetchInsurances: 'Could not fetch insurances. Please try again.',
            openPrePurchaseInformationInNewTab:
              'Pre-Purchase Information and Terms (opens in new tab)',
            insuranceIncludes: 'The insurance includes',
            addInsuranceHeading: 'Add',
            financialOptions: {
              otherFinancialOptions: 'Other financing options',
              privateLease: 'PrivateLease',
              businessLease: 'Etagslease',
              monthlyCost: 'Monthly cost',
              bindingTime: 'Binding Time',
              annualMileage: 'Annual Mileage',
              payInInstallments: 'Pay In Installments',
              payInInstallmentsDetailed: 'Partially pay {{amount}} in {{duration}} months',
              disclaimer: 'Calculated at {{interest}} interest.',
              loanOptions: 'Loan Options',
              downPayment: 'Cash Deposit',
              duration: 'Instalment',
              residual: 'Residual debt',
              interest: 'Interest',
              effectiveinterest: 'Effective interest',
              setupFee: 'Setup cost',
              administrationFee: 'Administrative fees',
              totalCreditCost: 'Total Credit Cost',
              loanOptionsDisclaimer:
                'Calculated at {{interest}} % interest (effective {{effectiveInterest}} %).',
              loanOptionsDisclaimerMileage:
                'Calculated on {{interest}} % interest (effective {{effectiveInterest}} %) and an annual mileage of {{mileage}} miles.',
            },
            actions: {
              emailSubject: "{{hostName}} - I'm interested in {{vehicle}}",
              openform: 'Open form',
              buyOnline: 'Buy the car online',
              showEmail: 'Show email address',
              showPhoneNumber: 'Show phone number',
              callNumber: 'Call',
              sendEmailTo: 'Send an email to',
            },
            accessories: {
              accessories: 'Accessories',
              accessoriesDescription: 'Accessories suitable for this vehicle.',
              price: 'Cash price',
              oldPrice: 'Previous Price',
              installationCost: 'Installation cost',
              articleNumberShort: 'Art. no.',
              moreInformationAboutAccessory: 'More information about {{accessory}}',
            },
          },
          productCard: {
            price: 'Price',
            privateLeasing: 'Private leasing',
            priceExclVat: 'Excl. VAT',
          },
          search: {
            filter: 'Filter',
            sort: 'Sort',
            showMore: {
              text: 'Show more',
              title: 'Show more results',
            },
            filterButton: {
              text: 'Filter',
              title: 'Show filter',
            },
            sortButton: {
              text: 'Sort',
              title: 'Select sort order',
            },
            searchResult: 'Search Result',
            types: {
              cars: 'Cars',
              cmsContent: 'Content',
            },
            snackbarNoSearchResults: {
              heading: 'No results',
              body: 'The search returned no results',
            },
            snackBarClearFreeText: {
              heading: 'No results',
              body: 'Clear the free text to see all filters',
            },
            sortOptions: {
              relevance: 'Relevance',
              publishedAsc: 'Published (oldest)',
              publishedDesc: 'Published (latest)',
              priceAsc: 'Price (Lowest)',
              priceDesc: 'Price (Maximum)',
              modelAsc: 'Year model (oldest)',
              modelDesc: 'Year Model (Newest)',
              mileageAsc: 'Mileage (lowest)',
              mileageDesc: 'Mileage (maximum)',
              leasingAsc: 'Private leasing (lowest)',
              leasingDesc: 'Private leasing (highest)',
              companyLeasingAsc: 'Business leasing (lowest)',
              companyLeasingDesc: 'Business leasing (highest)',
            },
            clearAllFilters: {
              text: 'Clear all filters',
              title: 'Clear all filters',
            },
            showNumberOfCustomUnit: 'Show {{count}} {{unit}}',
            result: {
              cmsContent: {
                singular: 'Result',
                plural: 'Results',
              },
              cars: {
                singular: 'Car',
                plural: 'Cars',
              },
            },
            freeText: 'Free text',
            from: 'From',
            to: 'To',
            min: 'Min',
            max: 'Max',
          },
          searchBar: {
            title: 'Search',
            placeholder: 'Search your car',
            button: {
              text: 'Search',
              title: 'Search',
            },
            searchResults: 'Search Results',
            showAllRsults: 'Show all cars',
          },
          lead: {
            openForm: 'Open form',
            messageFormTitle: 'Send message',
            callFormTitle: 'Get a callback',
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Email address',
            phoneNumber: 'Phone number',
            message: 'Message',
            tradeInCar: 'Trade-in car',
            registrationNumber: 'Registration number',
            mileage: 'Mileage',
            send: 'Send',
            close: 'Close',
            validationMessages: {
              requiredFirstName: 'First name is required',
              requiredLastName: 'Last name is required',
              requiredEmail: 'Email address is required',
              invalidEmail: 'Invalid email address',
              maxMessage: 'Message cannot exceed 500 characters',
              requiredRegNo: 'Registration number is required if you have a trade-in car',
              invalidRegNo: 'Invalid registration number',
              requiredMileage: 'Mileage is required if you have a trade-in car',
              requiredPhoneNumber: 'Phone number is required',
            },
            confirmationTitle: 'Thank you!',
            confirmationMessage:
              'Your message has been sent successfully. We will contact you soon.',
            confirmationClose: 'OK',
          },
          other: {
            loading: 'Loading...',
            error404: '404',
            showingNumberOfResults_one: 'Showing {{current}} of {{count}} results',
            showingNumberOfResults_other: 'Showing {{current}} of {{count}} results',
            closeModal: 'Close Modal',
            closePanel: 'Close panel',
            goToHeading: 'Go to {{heading}}',
            openDoumentt: 'Open {{document}}',
            previousImage: 'Previous Image',
            nextImage: 'Next Image',
            goToImageIndex: 'Go to image {{index}}',
            phoneNumber: 'Phone Number',
            showPhoneNumber: 'Show phone number',
            callPhoneNumber: 'Call {{phoneNumber}}',
            price: 'Price',
            privateLeasingPrice: 'Private Leasing',
            priceExclVat: 'Excl. VAT',
            video: 'Video',
            playVideo: 'Play video',
            searchResults: 'Search Results',
            showAllCars: 'Show all cars',
          },
        },
      },
    },
  });
};

ready(initializeLocale);
